import api from './api';
import { API_URL } from '../config';

export interface QrScanLog {
  id: number;
  qr_campaign_id: number;
  device_type: 'ios' | 'android' | 'desktop' | 'other';
  user_agent: string | null;
  ip_address: string | null;
  referrer: string | null;
  scanned_at: string;
}

export interface QrCampaign {
  id: number;
  name: string;
  code: string;
  description: string | null;
  source: string;
  location: string | null;
  ios_url: string | null;
  android_url: string | null;
  web_url: string | null;
  primary_color: string;
  has_logo: boolean;
  scans_count: number;
  ios_scans_count: number;
  android_scans_count: number;
  desktop_scans_count: number;
  conversions_count: number;
  is_active: boolean;
  created_by: number | null;
  created_at: string;
  updated_at: string;
  short_url?: string;
  creator?: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
  };
  recent_logs?: QrScanLog[];
}

export interface QrGlobalStats {
  total_campaigns: number;
  active_campaigns: number;
  total_scans: number;
  total_ios_scans: number;
  total_android_scans: number;
  total_desktop_scans: number;
  total_conversions: number;
  recent_scans: Array<QrScanLog & { campaign?: Partial<QrCampaign> }>;
}

export interface StoreSettings {
  iosUrl: string;
  androidUrl: string;
  webUrl: string;
}

export interface QrCampaignFilters {
  search?: string;
  source?: string;
  is_active?: boolean | string;
  page?: number;
  limit?: number;
}

export interface QrCampaignsResponse {
  success: boolean;
  data: QrCampaign[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

const qrCampaignService = {
  /**
   * Récupère la liste paginée des campagnes QR
   */
  async getCampaigns(filters: QrCampaignFilters = {}): Promise<QrCampaignsResponse> {
    const params = new URLSearchParams();
    if (filters.search) params.append('search', filters.search);
    if (filters.source) params.append('source', filters.source);
    if (filters.is_active !== undefined) params.append('is_active', String(filters.is_active));
    if (filters.page) params.append('page', String(filters.page));
    if (filters.limit) params.append('limit', String(filters.limit));

    const response = await api.get(`/admin/qr-campaigns?${params.toString()}`);
    return response.data;
  },

  /**
   * Récupère les statistiques globales
   */
  async getGlobalStats(): Promise<QrGlobalStats> {
    const response = await api.get('/admin/qr-campaigns/stats');
    return response.data.data;
  },

  /**
   * Récupère les paramètres globaux des stores
   */
  async getStoreSettings(): Promise<StoreSettings> {
    const response = await api.get('/admin/qr-campaigns/settings');
    return response.data.data;
  },

  /**
   * Met à jour les paramètres globaux des stores
   */
  async updateStoreSettings(settings: StoreSettings): Promise<StoreSettings> {
    const response = await api.put('/admin/qr-campaigns/settings', settings);
    return response.data.data;
  },

  /**
   * Récupère le détail d'une campagne par son ID
   */
  async getCampaignById(id: number): Promise<QrCampaign> {
    const response = await api.get(`/admin/qr-campaigns/${id}`);
    return response.data.data;
  },

  /**
   * Crée une nouvelle campagne QR
   */
  async createCampaign(data: Partial<QrCampaign>): Promise<QrCampaign> {
    const response = await api.post('/admin/qr-campaigns', data);
    return response.data.data;
  },

  /**
   * Met à jour une campagne QR existante
   */
  async updateCampaign(id: number, data: Partial<QrCampaign>): Promise<QrCampaign> {
    const response = await api.put(`/admin/qr-campaigns/${id}`, data);
    return response.data.data;
  },

  /**
   * Supprime une campagne QR
   */
  async deleteCampaign(id: number): Promise<void> {
    await api.delete(`/admin/qr-campaigns/${id}`);
  },

  /**
   * URL de l'image QR code (pour affichage ou téléchargement)
   */
  getQrImageUrl(campaignIdOrCode: number | string, options: { format?: 'png' | 'svg'; download?: boolean; width?: number; color?: string } = {}): string {
    const apiBase = API_URL;
    const params = new URLSearchParams();
    if (options.format) params.append('format', options.format);
    if (options.download) params.append('download', 'true');
    if (options.width) params.append('width', String(options.width));
    if (options.color) params.append('color', options.color);

    return `${apiBase}/qr-campaigns/image/${campaignIdOrCode}?${params.toString()}`;
  }
};

export default qrCampaignService;
